import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { toast } from 'sonner';
import { cn } from "@/lib/utils"; 
import { 
  ArrowUpRight, ArrowDownLeft, Scale, Eye, List, TrendingUp, X, Calendar as CalendarIcon 
} from 'lucide-react';
import DetalhesVendaModal from '../../components/DetalhesVendaModal'; 

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { 
  useFinanceiroDropdownData, 
  initialCaixaFilters, 
  CATEGORIA_PAGAMENTO, 
  CATEGORIA_RECEBIMENTO, 
  getParamsForAPI,
  staticDateRange 
} from '@/services/FinanceiroService';


export default function FluxoCaixaView() {
  const [fluxoCaixa, setFluxoCaixa] = useState(null); 
  const [loadingFluxoCaixa, setLoadingFluxoCaixa] = useState(true);
  
  const [caixaFilters, setCaixaFilters] = useState(initialCaixaFilters);
  
  const { contasList, todasCategorias, loading: loadingDropdowns } = useFinanceiroDropdownData();
  
  const [isDetalhesModalOpen, setIsDetalhesModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null); 
  const [selectedVendaData, setSelectedVendaData] = useState(null); 
  const [loadingModal, setLoadingModal] = useState(false);
  
  // --- Funções de Carregamento ---

  const loadFluxoCaixa = async (filtersToLoad = caixaFilters) => {
    setLoadingFluxoCaixa(true);
    try {
      // 1. Pega parâmetros básicos
      const params = getParamsForAPI(filtersToLoad) || {}; 

      // 2. Processamento Manual e Seguro das Datas
      if (filtersToLoad.date?.from) {
          const d = new Date(filtersToLoad.date.from);
          // Verifica se a data é válida
          if (!isNaN(d.getTime())) {
              // Zera o horário para o inicio do dia (UTC)
              const startDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
              params.data_inicio = startDate.toISOString();
          }
      } else {
          // Garante que não enviamos lixo se estiver vazio
          delete params.data_inicio;
      }

      if (filtersToLoad.date?.to) {
          const d = new Date(filtersToLoad.date.to);
          if (!isNaN(d.getTime())) {
              // Ajusta para o final do dia (UTC)
              const endDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999));
              params.data_fim = endDate.toISOString();
          }
      } else {
          delete params.data_fim;
      }

      // 3. Outros filtros
      if (filtersToLoad.termo_busca) params.termo_busca = filtersToLoad.termo_busca;
      if (filtersToLoad.conta_id && filtersToLoad.conta_id !== 'all') params.conta_id = filtersToLoad.conta_id;
      if (filtersToLoad.categoria && filtersToLoad.categoria !== 'all') params.categoria = filtersToLoad.categoria;

      console.log("📡 Enviando Request Fluxo Caixa com params:", params); // OLHE O CONSOLE (F12)

      const res = await axiosInstance.get('/fluxo-caixa', { params });
      setFluxoCaixa(res.data); 
      
    } catch (error) {
      console.error("Erro request:", error);
      toast.error('Erro ao carregar fluxo de caixa.');
      setFluxoCaixa({ total_entradas: 0, total_saidas: 0, saldo_periodo: 0, itens_caixa: [], num_transacoes: 0, media_ganhos: 0, media_gastos: 0 }); 
    } finally {
      setLoadingFluxoCaixa(false);
    }
  };
  
  // Carga inicial
  useEffect(() => {
    loadFluxoCaixa(initialCaixaFilters);
  }, []); 

  // Handlers
  const handleFiltrarClick = () => {
    loadFluxoCaixa();
  };
  
  const handleLimparFiltrosCaixa = () => {
    setCaixaFilters(initialCaixaFilters);
    loadFluxoCaixa(initialCaixaFilters);
  };
  
  const handleDateRangeChange = (range) => {
    // Log para depuração: veja o que o componente está enviando
    console.log("DateRangePicker retornou:", range);

    // Proteção: se range for undefined (limpar filtro), define como nulo
    if (!range) {
        setCaixaFilters(prev => ({
            ...prev,
            date: { from: null, to: null }
        }));
        return;
    }

  // Tenta capturar 'from'/'to' OU 'startDate'/'endDate' (caso o componente use nomes diferentes)
    const newFrom = range.from || range.startDate || null;
    const newTo = range.to || range.endDate || null;

    setCaixaFilters(prev => ({
      ...prev,
      date: { 
        from: newFrom, 
        to: newTo
      }
    }));
  };

  const handleShowDetails = async (item) => {
    setSelectedItem(item); 
    setIsDetalhesModalOpen(true); 
    
    if (item.venda_id) {
      setLoadingModal(true);
      try {
        const res = await axiosInstance.get(`/vendas/${item.venda_id}`);
        setSelectedVendaData(res.data); 
      } catch (error) {
        toast.error("Erro ao buscar detalhes da venda.");
        setIsDetalhesModalOpen(false); 
      } finally {
        setLoadingModal(false);
      }
    } else {
      setSelectedVendaData(null); 
    }
  };

  const handleCloseModals = () => {
    setIsDetalhesModalOpen(false);
    setSelectedItem(null);
    setSelectedVendaData(null);
  };

  const formatRangeDisplay = () => {
    const from = caixaFilters.date?.from;
    const to = caixaFilters.date?.to;

    if (!from) return 'Selecione o período';
    
    const fromDate = typeof from === 'string' ? parseISO(from) : from;
    const toDate = typeof to === 'string' ? parseISO(to) : to;

    if (from && !to) {
      return format(fromDate, "dd/MM/yyyy", { locale: ptBR });
    }
    
    return `${format(fromDate, "dd/MM/yyyy", { locale: ptBR })} - ${format(toDate, "dd/MM/yyyy", { locale: ptBR })}`;
  };

  const isLoading = loadingFluxoCaixa || loadingDropdowns;

  if (isLoading && !fluxoCaixa) { 
    return (
      <div>
        <div className="container mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">A carregar dados financeiros...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2 pt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Fluxo de Caixa (Extrato)
        </h1>
        <p className="text-gray-600">Liquidez e movimentações registradas.</p>
      </div>
      
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <ArrowUpRight className="w-6 h-6 text-green-600 mb-2" />
          <h3 className="text-xs font-medium text-gray-500">Total de Ganhos</h3>
          <p className="text-xl font-bold text-green-600 mt-1">
            {isLoading ? '...' : `R$ ${(fluxoCaixa?.total_entradas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          </p>
        </div>
        <div className="stat-card">
          <ArrowDownLeft className="w-6 h-6 text-red-600 mb-2" />
          <h3 className="text-xs font-medium text-gray-500">Total de Gastos</h3>
          <p className="text-xl font-bold text-red-600 mt-1">
            {isLoading ? '...' : `R$ ${(fluxoCaixa?.total_saidas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          </p>
        </div>
        
        <div className="stat-card">
          <Scale className="w-6 h-6 text-blue-600 mb-2" />
          <h3 className="text-xs font-medium text-gray-500">Saldo do Período</h3>
          <p className={`text-xl font-bold ${ (fluxoCaixa?.saldo_periodo || 0) >= 0 ? 'text-blue-600' : 'text-red-600'} mt-1`}>
            {isLoading ? '...' : `R$ ${(fluxoCaixa?.saldo_periodo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          </p>
        </div>

        <div className="stat-card">
          <TrendingUp className="w-6 h-6 text-green-600 mb-2" />
          <h3 className="text-xs font-medium text-gray-500">Média de Ganhos</h3>
          <p className="text-xl font-bold text-green-600 mt-1">
            {isLoading ? '...' : `R$ ${(fluxoCaixa?.media_ganhos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          </p>
        </div>
        <div className="stat-card">
          <TrendingUp className="w-6 h-6 text-red-600 mb-2" style={{ transform: 'scaleY(-1)' }}/>
          <h3 className="text-xs font-medium text-gray-500">Média de Gastos</h3>
          <p className="text-xl font-bold text-red-600 mt-1">
            {isLoading ? '...' : `R$ ${(fluxoCaixa?.media_gastos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          </p>
        </div>
        <div className="stat-card">
          <List className="w-6 h-6 text-gray-500 mb-2" />
          <h3 className="text-xs font-medium text-gray-500">N° de Transações</h3>
          <p className="text-xl font-bold text-gray-900 mt-1">
            {isLoading ? '...' : (fluxoCaixa?.num_transacoes || 0)}
          </p>
        </div>
      </div>

      {/* Filtros de Análise */}
      <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Filtros de Análise</h3>
        <p className="text-sm text-gray-600 mb-4">Configure os filtros para analisar o fluxo de caixa no período desejado.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="termo_busca">Pesquisar por título</Label>
            <Input 
              id="termo_busca"
              placeholder="Digite para pesquisar..." 
              value={caixaFilters.termo_busca}
              onChange={(e) => setCaixaFilters(prev => ({...prev, termo_busca: e.target.value}))}
            />
          </div>
          <div>
            <Label htmlFor="filtro_banco">Filtrar por banco</Label>
            <Select
              value={caixaFilters.conta_id}
              onValueChange={(value) => setCaixaFilters(prev => ({...prev, conta_id: value}))}
            >
              <SelectTrigger id="filtro_banco"><SelectValue placeholder="Todos os bancos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os bancos</SelectItem>
                {contasList.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="filtro_categoria">Filtrar por categoria</Label>
            <Select
              value={caixaFilters.categoria}
              onValueChange={(value) => setCaixaFilters(prev => ({...prev, categoria: value}))}
            >
              <SelectTrigger id="filtro_categoria"><SelectValue placeholder="Todas as categorias" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {todasCategorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Alterar Período</Label>
            <DateRangePicker
              startDate={caixaFilters.date.from}
              endDate={caixaFilters.date.to}
              onChange={handleDateRangeChange}
              placeholder="Selecione o período"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={handleLimparFiltrosCaixa}><X className="w-4 h-4 mr-2" /> Limpar</Button>
          <Button onClick={handleFiltrarClick} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            Filtrar
          </Button>
        </div>
      </div>
      
      {/* Tabela do Extrato (Fluxo de Caixa) */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Extrato de Movimentações</h3>
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Liquidação</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y">
              {loadingFluxoCaixa ? (
                <tr>
                  <td colSpan="5" className="text-center p-6 text-gray-500">A carregar fluxo de caixa...</td>
                </tr>
              ) : !fluxoCaixa || fluxoCaixa.itens_caixa.length === 0 ? ( 
                <tr>
                  <td colSpan="5" className="text-center p-6 text-gray-500">
                    Nenhuma movimentação de caixa encontrada no período.
                  </td>
                </tr>
              ) : (
                fluxoCaixa.itens_caixa.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">{new Date(item.data).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.descricao}</td>
                    <td className="px-4 py-3 text-sm">
                      {item.tipo === 'ENTRADA' ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Entrada</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Saída</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${item.tipo === 'ENTRADA' ? 'text-green-600' : 'text-red-600'}`}>
                      {item.tipo === 'ENTRADA' ? '+' : '-'} R$ {item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleShowDetails(item)} 
                        title="Ver Detalhes"
                      >
                        <Eye className="w-4 h-4 text-blue-600" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modais */}
      {selectedItem && selectedItem.venda_id && (
        <DetalhesVendaModal
          venda={selectedVendaData} 
          isOpen={isDetalhesModalOpen}
          onClose={handleCloseModals}
          loading={loadingModal}
        />
      )}

      {selectedItem && !selectedItem.venda_id && (
        <Dialog open={isDetalhesModalOpen} onOpenChange={handleCloseModals}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Detalhe do Lançamento Avulso</DialogTitle>
              <DialogDescription>
                Este é um lançamento financeiro que não está atrelado a uma venda.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs text-gray-500">Descrição</Label>
                <p className="font-medium">{selectedItem.descricao}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Valor</Label>
                  <p className={`font-medium ${selectedItem.tipo === 'ENTRADA' ? 'text-green-600' : 'text-red-600'}`}>
                    R$ {selectedItem.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Tipo</Label>
                  <p className="font-medium">{selectedItem.tipo}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Data de Liquidação</Label>
                  <p className="font-medium">{new Date(selectedItem.data).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Categoria</Label>
                  <p className="font-medium">{selectedItem.categoria || 'N/A'}</p>
                </div>
              </div>
            </div>
            <Button onClick={handleCloseModals} className="w-full mt-4">Fechar</Button>
          </DialogContent>
        </Dialog>
      )}
      
    </div>
  );
}